import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAmendments } from 'actions/expiryRenewalActions';
import { Member, MemberAddAmendment, MemberAmendment, MemberModifyAmendment } from 'types/expiryRenewal';
import { Store } from 'types/store';
import { findMemberAmendment, toDatePart, toIsoDateOfBirth } from '../utils/amendmentUtils';
import useMemberForm, { MemberFields, TITLE_OPTIONS } from './useMemberForm';

export type { MemberFields, MemberFieldErrors, TouchedMemberFields } from './useMemberForm';

/**
 * A title the quote returned that the dropdown does not offer cannot be shown in it, so
 * the control seeds blank and the member has to pick one before saving - which the
 * Required rule on Title already enforces.
 */
const seedTitle = (title: string): string => ((TITLE_OPTIONS as readonly string[]).includes(title) ? title : '');

/**
 * Stages an edit to a member as a Member/Modify amendment - or, when the member being
 * edited was itself added during this journey, by rewriting that ExtraPerson/Add in
 * place. The mirror of useEditVehicle, over the form state in useMemberForm.
 */
const useEditMember = (member: Member, onClose: () => void) => {
  const dispatch = useDispatch();
  const pageModel = useSelector((state: Store) => state.expiryRenewal.pageModel);
  const existingAmendments = useSelector((state: Store) => state.expiryRenewal.amendments);

  const priorAmendment = findMemberAmendment(member, existingAmendments);

  const priorFields = priorAmendment?.amendmentType === 'Add'
    ? priorAmendment.memberAdd
    : priorAmendment?.memberAmend;

  const initialFields: MemberFields = {
    Title: seedTitle(priorFields?.title ?? member.title),
    FirstName: priorFields?.firstName ?? member.firstName,
    LastName: priorFields?.surname ?? member.surname,
    // A member the quote holds no date of birth for starts blank, and the Required rule
    // then blocks the save until one is entered - same as adding a member.
    DateOfBirth: priorFields || member.hasDateOfBirth
      ? toDatePart(priorFields?.dateOfBirth ?? member.dateOfBirth)
      : '',
  };

  const initialFieldsRef = useRef(initialFields);

  const { fields, errors, touched, hasErrors, setTouched, handleChange, handleBlur } = useMemberForm(initialFields);

  const isSaveDisabled = hasErrors;

  const handleSave = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ Title: true, FirstName: true, LastName: true, DateOfBirth: true });
    if (hasErrors || !pageModel) return;

    const initial = initialFieldsRef.current;

    const isSame = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

    const hasChanges = !isSame(fields.Title, initial.Title)
      || !isSame(fields.FirstName, initial.FirstName)
      || !isSame(fields.LastName, initial.LastName)
      || !isSame(fields.DateOfBirth, initial.DateOfBirth);

    if (hasChanges) {
      const matchedAmendment = findMemberAmendment(member, existingAmendments);
      const otherAmendments = existingAmendments.filter((a) => a !== matchedAmendment);

      const memberBase = {
        title: fields.Title,
        firstName: fields.FirstName.trim(),
        surname: fields.LastName.trim(),
        dateOfBirth: toIsoDateOfBirth(fields.DateOfBirth),
      };

      const newAmendment: MemberAmendment = matchedAmendment?.amendmentType === 'Add'
        ? ({
          productCode: 'ExtraPerson',
          amendmentType: 'Add',
          memberAdd: memberBase,
        } as MemberAddAmendment)
        : ({
          productCode: 'Member',
          amendmentType: 'Modify',
          memberAmend: {
            ...memberBase,
            /**
             * Carried over from any earlier edit, so a member edited twice still names
             * itself by the details the quote returned. The date of birth is echoed
             * verbatim rather than reformatted - the BE compares its date component and
             * the quote's own value is the one guaranteed to match.
             */
            oldFirstName: (matchedAmendment as MemberModifyAmendment | undefined)?.memberAmend.oldFirstName ?? member.firstName,
            oldSurname: (matchedAmendment as MemberModifyAmendment | undefined)?.memberAmend.oldSurname ?? member.surname,
            oldDateOfBirth: (matchedAmendment as MemberModifyAmendment | undefined)?.memberAmend.oldDateOfBirth ?? member.dateOfBirth,
          },
        } as MemberModifyAmendment);

      dispatch(setAmendments([...otherAmendments, newAmendment]));
    }

    onClose();
  }, [fields, hasErrors, pageModel, member, existingAmendments, dispatch, onClose, setTouched]);

  return { fields, errors, touched, isSaveDisabled, handleChange, handleBlur, handleSave };
};

export default useEditMember;
